import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injector, Signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  InitiateWithdrawRequest,
  WithdrawControllerService,
  WithdrawInfoDto,
} from '@api/client';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStoreFeature,
  type,
  withComputed,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { NotificationService } from '@shared/services/notifications/notifications.service';
import { mergeMap, pipe, switchMap } from 'rxjs';

export type WithdrawalStatus = 'idle' | 'pending' | 'success' | 'error';

export const useWithdrawals = () =>
  signalStoreFeature(
    {
      props: {
        _activeAccountId: type<Signal<number | undefined>>(),
        _injector: type<Injector>(),
        _dialogService: type<MatDialog>(),
        _notificationService: type<NotificationService>(),
      },
      methods: {
        loadBalance: type<() => void>(),
      },
    },
    withState({
      withdrawalDetails: new Array<WithdrawInfoDto>(),
      withdrawalDetailsLoading: false,
      withdrawalStatus: 'idle' as WithdrawalStatus,
    }),
    withProps(() => ({
      _withdrawalService: inject(WithdrawControllerService),
    })),
    withComputed(store => {
      const withdrawalAddresses = computed(() => store.withdrawalDetails().flat());

      return { withdrawalAddresses };
    }),
    withMethods(store => {
      const loadWithdrawalDetails = rxMethod<string>(
        pipe(
          switchMap(assetCode => {
            patchState(store, { withdrawalDetails: [], withdrawalDetailsLoading: true });

            return store._withdrawalService
              .getWithdrawalInfo(assetCode, store._activeAccountId())
              .pipe(
                tapResponse({
                  next: (withdrawalDetails: WithdrawInfoDto[]) =>
                    patchState(store, {
                      withdrawalDetails,
                      withdrawalDetailsLoading: false,
                    }),
                  error: (_error: HttpErrorResponse) =>
                    patchState(store, { withdrawalDetailsLoading: false }),
                }),
              );
          }),
        ),
      );

      const initiateWithdrawal = rxMethod<InitiateWithdrawRequest>(
        pipe(
          mergeMap(request => {
            patchState(store, { withdrawalStatus: 'pending' });

            return store._withdrawalService
              .initiate({ ...request, accountId: store._activeAccountId() })
              .pipe(
                tapResponse({
                  next: () => {
                    patchState(store, { withdrawalStatus: 'success' });
                    store._notificationService.showSuccessNotification(
                      'NOTIFICATION.THE_WITHDRAWAL_HAS_BEEN_INITIATED_SUCCESSFULLY',
                    );
                    store.loadBalance();
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(store, { withdrawalStatus: 'error' });
                    store._notificationService.showErrorNotification(
                      'NOTIFICATION.THE_WITHDRAWAL_COULDNT_BE_INITIATED',
                      error,
                    );
                  },
                }),
              );
          }),
        ),
      );

      const openWithdrawDialog = async () => {
        const { WithdrawDialogComponent } =
          await import('app/dashboard/components/withdraw-dialog/withdraw-dialog.component');

        patchState(store, { withdrawalStatus: 'idle' });

        store._dialogService.open(WithdrawDialogComponent, {
          disableClose: true,
          injector: store._injector,
        });
      };

      return { loadWithdrawalDetails, initiateWithdrawal, openWithdrawDialog };
    }),
  );
